import { z } from 'zod';

export const idSchema = z.string().uuid();

export const emailSchema = z
  .string()
  .min(3, 'Email is too short')
  .max(254, 'Email is too long')
  .email('Enter a valid email');

export const phoneSchema = z
  .string()
  .trim()
  .min(7, 'Phone is too short')
  .max(20, 'Phone is too long')
  .regex(/^[+0-9\s().-]+$/u, 'Phone contains invalid characters');

export const nameSchema = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(120, 'Name is too long');

export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .max(128, 'Password is too long')
  .refine((value) => /[A-Z]/u.test(value), 'Password must contain an uppercase letter')
  .refine((value) => /[a-z]/u.test(value), 'Password must contain a lowercase letter')
  .refine((value) => /[0-9]/u.test(value), 'Password must contain a number');

export const turnstileTokenSchema = z
  .string()
  .min(1, 'Turnstile token is required')
  .max(2048, 'Turnstile token is too long');

export const paginationSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
  })
  .strict();

export type Pagination = z.infer<typeof paginationSchema>;

export const sortOrderSchema = z.enum(['asc', 'desc']).default('asc');

export const sortSchema = <const Field extends string>(fields: readonly Field[]) =>
  z
    .object({
      sortBy: z.enum(fields as unknown as [Field, ...Field[]]).optional(),
      sortOrder: sortOrderSchema.optional(),
    })
    .strict();

export const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Date must be in YYYY-MM-DD format');

export const isoDateTimeSchema = z.string().datetime({ offset: true });

export const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;
